import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, getToken, API } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { rupee } from "@/lib/constants";
import { toast } from "sonner";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { ChevronLeft, Loader2, UserPlus, Star } from "lucide-react";
import { fmtTs } from "@/components/Timeline";
import { PaymentPanel, CancelRefundPanel } from "@/components/admin/BookingMoney";

const STATUSES = [
    "VISIT FEE PENDING", "VISIT FEE PAYMENT PENDING", "VISIT FEE PAID",
    "REQUEST RECEIVED", "PROFESSIONAL ASSIGNED", "CONFIRMED", "ON THE WAY", "ARRIVED",
    "INSPECTION", "ESTIMATE APPROVAL REQUIRED", "WORK IN PROGRESS",
    "CUSTOMER REVIEW", "REVISION REQUIRED", "WORK DONE - PAYMENT PENDING", "COMPLETED",
    "PAYMENT PENDING", "PAID", "CLOSED",
];

export default function AdminBookingDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [b, setB] = useState(null);
    const [pros, setPros] = useState([]);
    const [busy, setBusy] = useState(false);
    const [assignId, setAssignId] = useState("");
    const [newStatus, setNewStatus] = useState("");
    const [est, setEst] = useState({ visit_charge: 99, labour: 0, materials: 0, discount: 0, other: 0 });

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/bookings/${id}`); setB(data);
            if (data.estimate) setEst({ visit_charge: data.estimate.visit_charge, labour: data.estimate.labour, materials: data.estimate.materials, discount: data.estimate.discount, other: data.estimate.other });
        } catch { toast.error("Could not load"); }
    }, [id]);

    useEffect(() => { load(); api.get(`/admin/professionals?booking_id=${id}`).then((r) => setPros(r.data)).catch(() => {}); }, [load, id]);
    useLiveRefresh(() => { api.get(`/bookings/${id}`).then((r) => setB(r.data)).catch(() => {}); }, 15000);

    if (!b) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-orange" /></div>;

    const run = async (fn, msg) => { setBusy(true); try { await fn(); toast.success(msg); await load(); } catch (err) { toast.error(apiError(err.response?.data?.detail)); } finally { setBusy(false); } };
    const assign = () => assignId && run(() => api.post(`/bookings/${id}/assign`, { professional_id: assignId }), "Professional assigned");
    const changeStatus = () => newStatus && run(() => api.patch(`/bookings/${id}/status`, { status: newStatus }), "Status updated");
    const submitEst = () => run(() => api.post(`/bookings/${id}/estimate`, { visit_charge: +est.visit_charge, labour: +est.labour, materials: +est.materials, discount: +est.discount, other: +est.other }), "Estimate submitted");
    const confirmVisitFee = () => run(() => api.post(`/bookings/${id}/confirm-visit-fee`), "Visit fee confirmed");
    const confirmServicePayment = () => run(() => api.post(`/bookings/${id}/confirm-payment`), "Payment confirmed — job completed");
    const estTotal = (+est.visit_charge) + (+est.labour) + (+est.materials) + (+est.other) + 9 - (+est.discount);
    const locked = ["PAID", "COMPLETED", "CLOSED", "CANCELLED"].includes(b.status);

    return (
        <div className="max-w-4xl">
            <button onClick={() => nav(-1)} className="mb-4 flex items-center gap-1 text-sm font-semibold text-navy/60 hover:text-navy"><ChevronLeft size={18} /> Back to bookings</button>

            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h1 className="font-heading text-2xl font-extrabold text-navy">{b.booking_id}</h1>
                    <p className="text-sm text-navy/55">{b.service_name} · {b.problem}</p>
                </div>
                <StatusBadge status={b.status} />
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
                {/* Info */}
                <Card title="Customer & Service">
                    <Row l="Customer" v={b.name} />
                    <Row l="Phone" v={b.phone} />
                    <Row l="Address" v={b.address_line} />
                    <Row l="Area" v={b.area_name} />
                    <Row l="Society" v={b.society} />
                    <Row l="When" v={`${b.preferred_date} · ${b.preferred_window}`} />
                    <Row l="Source" v={b.source} />
                    {b.referral_code && <Row l="Referral" v={b.referral_code} />}
                    {b.rating && <Row l="Rating" v={<span className="inline-flex items-center gap-1"><Star size={13} className="fill-orange text-orange" />{b.rating.stars}</span>} />}
                </Card>

                {/* Assign + status */}
                <Card title="Operations">
                    {locked ? <p data-testid="admin-ops-locked" className="text-xs text-navy/55">This booking is {b.status.toLowerCase()} — assignment, status and estimate are locked. Use refunds below for any money correction.</p> : (<>
                    <label className="mb-1 block text-xs font-semibold text-navy/60">Assign / reassign professional</label>
                    <div className="flex gap-2">
                        <select data-testid="assign-pro-select" value={assignId} onChange={(e) => setAssignId(e.target.value)} className="flex-1 rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange">
                            <option value="">Select {b.service_name} professional</option>
                            {pros.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.pro_id}) · ⭐{p.rating || "New"}</option>)}
                        </select>
                        <button data-testid="assign-pro-btn" disabled={busy} onClick={assign} className="flex items-center gap-1.5 rounded-xl bg-navy px-4 text-sm font-bold text-white disabled:opacity-60"><UserPlus size={16} /></button>
                    </div>
                    {b.professional && <p className="mt-1.5 text-xs text-navy/55">Current: <b className="text-navy">{b.professional.name}</b> ({b.professional.pro_id})</p>}
                    <p className="mt-1 text-[11px] text-navy/45">Only verified pros offering this service are listed. New jobs auto-pop to nearby online pros; manual assign unlocks after 5 min without acceptance, or anytime when the customer has an open complaint.</p>

                    <label className="mb-1 mt-4 block text-xs font-semibold text-navy/60">Change status</label>
                    <div className="flex gap-2">
                        <select data-testid="status-select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="flex-1 rounded-xl border border-navy/15 bg-white px-3 py-2.5 text-sm outline-none focus:border-orange">
                            <option value="">Select status</option>
                            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button data-testid="update-status-btn" disabled={busy} onClick={changeStatus} className="rounded-xl bg-navy px-4 text-sm font-bold text-white disabled:opacity-60">Set</button>
                    </div>
                    </>)}
                    <CancelRefundPanel b={b} onChanged={load} />
                </Card>

                {/* Estimate */}
                <Card title="Estimate">
                    {b.estimate && <p className="mb-2 text-xs text-navy/55">Status: <b className={b.estimate.status === "APPROVED" ? "text-emerald-600" : b.estimate.status === "REJECTED" ? "text-red-500" : "text-orange-dark"}>{b.estimate.status}</b></p>}
                    <div className="grid grid-cols-2 gap-2">
                        {[["visit_charge", "Visit"], ["labour", "Labour"], ["materials", "Materials"], ["other", "Convenience Fee"], ["discount", "Discount"]].map(([k, l]) => (
                            <div key={k}><label className="mb-1 block text-xs text-navy/50">{l}</label><input data-testid={`admin-est-${k}`} type="number" value={est[k]} onChange={(e) => setEst({ ...est, [k]: e.target.value })} className="w-full rounded-xl border border-navy/15 px-3 py-2 text-sm outline-none focus:border-orange" /></div>
                        ))}
                        <div className="flex items-end"><div className="w-full rounded-xl bg-navy/5 px-3 py-2 text-sm font-bold text-navy">Total {rupee(estTotal)} <span className="block text-[10px] font-semibold text-navy/45" data-testid="admin-est-platform-fee">incl. fixed platform fee ₹9</span></div></div>
                    </div>
                    <button data-testid="admin-submit-est-btn" disabled={busy || locked} onClick={submitEst} className="mt-3 w-full rounded-full bg-navy py-2.5 text-sm font-bold text-white disabled:opacity-60">Submit estimate</button>
                </Card>

                {/* Payment */}
                <Card title="Payment & Invoice">
                    {b.visit_fee_payment && (
                        <div className="mb-3 rounded-xl border border-navy/10 bg-navy/[0.02] p-3" data-testid="admin-visit-fee-block">
                            <p className="text-sm text-navy/60">Visit fee: <b className="text-navy">{rupee(b.visit_fee_payment.amount)}</b> via {b.visit_fee_payment.method} · <span className={["Paid", "CASH_CONFIRMED"].includes(b.visit_fee_payment.status) ? "text-emerald-600 font-bold" : "text-amber-600 font-bold"}>{b.visit_fee_payment.status}</span></p>
                            {["PendingConfirmation", "CASH_PENDING_CONFIRMATION"].includes(b.visit_fee_payment.status) && (
                                <button data-testid="confirm-visit-fee-btn" disabled={busy} onClick={confirmVisitFee} className="mt-2 w-full rounded-full bg-emerald-500 py-2 text-sm font-bold text-white disabled:opacity-60">Confirm visit fee</button>
                            )}
                        </div>
                    )}
                    {b.payment && ["PendingConfirmation", "CASH_PENDING_CONFIRMATION"].includes(b.payment.status) && (
                        <button data-testid="confirm-payment-btn" disabled={busy} onClick={confirmServicePayment} className="mb-3 w-full rounded-full bg-emerald-500 py-2.5 text-sm font-bold text-white disabled:opacity-60">Confirm payment ({rupee(b.payment.amount)}) & complete</button>
                    )}
                    <PaymentPanel b={b} onChanged={load} />
                </Card>
            </div>

            {/* evidence */}
            {b.evidence?.length > 0 && (
                <Card title="Service evidence (Before / After)" className="mt-4">
                    {["before", "after"].map((kind) => {
                        const items = b.evidence.filter((e) => e.kind === kind);
                        if (!items.length) return null;
                        return (
                            <div key={kind} className="mb-3" data-testid={`admin-evidence-${kind}`}>
                                <p className="mb-1.5 text-xs font-bold uppercase tracking-wide text-navy/50">{kind} work ({items.length})</p>
                                <div className="flex flex-wrap gap-2">
                                    {items.map((e) => (
                                        <a key={e.id} href={`${API}/files/${e.path}?auth=${getToken()}`} target="_blank" rel="noreferrer" className="relative" title={e.gps ? `GPS ${e.gps.lat}, ${e.gps.lng} (±${Math.round(e.gps.accuracy)}m) · ${e.location_status}` : e.location_status}>
                                            <img src={`${API}/files/${e.path}?auth=${getToken()}`} alt={kind} className="h-24 w-24 rounded-lg border border-navy/10 object-cover" />
                                            <span className={`absolute bottom-0 left-0 right-0 rounded-b-lg px-1 py-0.5 text-[8px] font-bold text-white ${e.location_verified ? "bg-emerald-600/80" : "bg-amber-600/80"}`}>{e.location_verified ? "GPS ✓" : "No GPS"}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </Card>
            )}

            {/* timeline */}
            <DispatchCard id={id} />
            <Card title="Status history (timestamped)" className="mt-4">
                <div className="space-y-1.5" data-testid="admin-booking-history">
                    {[...(b.status_history || [])].reverse().map((h, i) => (
                        <div key={i} className="flex items-start justify-between gap-3 border-b border-navy/5 pb-1.5 text-sm last:border-0">
                            <span><span className="font-semibold text-navy">{h.status}</span>{h.note && <span className="block text-xs text-navy/55">{h.note}</span>}</span>
                            <span className="shrink-0 text-right text-xs text-navy/40">{fmtTs(h.at)}{h.by && <span className="block">{h.by}{h.by_role ? ` (${h.by_role})` : ""}</span>}</span>
                        </div>
                    ))}
                </div>
            </Card>
        </div>
    );
}

function Card({ title, children, className = "" }) {
    return <div className={`rounded-2xl border border-navy/10 bg-white p-5 ${className}`}><h3 className="mb-3 font-heading font-bold text-navy">{title}</h3><div className="space-y-1.5 text-sm">{children}</div></div>;
}
function Row({ l, v }) {
    return <div className="flex justify-between gap-4"><span className="text-navy/50">{l}</span><span className="text-right font-medium text-navy">{v || "—"}</span></div>;
}

function DispatchCard({ id }) {
    const [d, setD] = useState(null);
    useLiveRefresh(() => { api.get(`/admin/bookings/${id}/dispatch`).then((r) => setD(r.data)).catch(() => {}); }, 15000);
    useEffect(() => { api.get(`/admin/bookings/${id}/dispatch`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
    if (!d || !d.dispatching) return null;
    return (
        <div className="mt-4 rounded-2xl border border-teal/30 bg-teal/5 p-4 text-sm" data-testid="admin-dispatch-card">
            <p className="font-heading font-bold text-navy">Nearby dispatch in progress · radius {d.radius_km} km (max {d.max_km})</p>
            <p className="text-xs text-navy/55">Offered to online verified pros within the radius; widens by 2 km every 2 min. {d.declined} declined.{!d.has_location && " No GPS for this address — using area centre if set."}</p>
            {d.nearby.length > 0 && <p className="mt-1 text-xs text-navy/70">Nearest: {d.nearby.slice(0, 5).map((p) => `${p.name} (${p.distance_km} km)`).join(" · ")}</p>}
        </div>
    );
}
